import { Chip, LabelChipVariant, TableCell, theme } from '@ketch-com/deck'
import { EventsByClassificationCount, ClassificationType } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { LinearProgress, Typography, Tooltip, Box } from '@mui/material'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { AssetsTabTableLabelChip } from 'pages/dataSystems/DataSystem/assetsTab/components/AssetsTabTableLabelChip'
import { SupportedClassificationTypes } from '../types'
import { DataClassificationDetails } from 'pages/dataSentry/interfaces'

export interface DataSentryEventsByClassificationGridUtilProps {
  classificationType: SupportedClassificationTypes
  totalCount?: number
  setDataClassificationDetails?: React.Dispatch<React.SetStateAction<DataClassificationDetails | null>>
}

const enumToHeaderCopyMap: Record<SupportedClassificationTypes, string> = {
  [ClassificationType.DATA_TYPE]: 'Data Type',
  [ClassificationType.DATA_CATEGORY]: 'Data Category',
}

const enumToLabelChipVariantMap: Record<SupportedClassificationTypes, LabelChipVariant> = {
  [ClassificationType.DATA_TYPE]: LabelChipVariant.classification,
  [ClassificationType.DATA_CATEGORY]: LabelChipVariant.category,
}

export const dataSentryEventsByClassificationGridUtil = (
  { classificationType, totalCount = 0 }: DataSentryEventsByClassificationGridUtilProps,
  setDataClassificationDetails: React.Dispatch<React.SetStateAction<DataClassificationDetails | null>>,
): GridColDef<EventsByClassificationCount>[] => {
  const columns: GridColDef<EventsByClassificationCount>[] = [
    {
      align: 'left',
      field: 'classificationType',
      headerName: enumToHeaderCopyMap[classificationType],
      flex: 2,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => {
        return (
          <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
            {params.colDef.headerName}
          </Typography>
        )
      },
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        return (
          <TableCell>
            <Box
              onClick={() => {
                let type: ClassificationType = ClassificationType.UNSPECIFIED
                if (classificationType === ClassificationType.DATA_CATEGORY) {
                  type = ClassificationType.DATA_CATEGORY
                } else if (classificationType === ClassificationType.DATA_TYPE) {
                  type = ClassificationType.DATA_TYPE
                }
                setDataClassificationDetails?.({
                  type,
                  name: row.classification?.name || '',
                  code: row.classification?.code || '',
                })
              }}
            >
              <AssetsTabTableLabelChip
                title={row.classification?.name || row.classification?.code}
                variant={enumToLabelChipVariantMap[classificationType]}
                hideConfidence
                hideIcon
                supportMultiLineTitle
                isViewOnly
              />
            </Box>
          </TableCell>
        )
      },
    },
    {
      align: 'left',
      field: 'precentage Graph',
      headerName: '',
      flex: 1,
      minWidth: 80,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography variant="smallLabel" sx={{}} color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        const percentage = Math.round(((row?.count || 0) / totalCount) * 100)
        return (
          <TableCell width="100%">
            {totalCount ? (
              <LinearProgress
                sx={{
                  borderRadius: 24,
                  height: 8,
                  flexGrow: '1',
                }}
                variant="determinate"
                value={percentage}
                style={{ width: '90%' }}
              />
            ) : (
              <EmptyValueRenderer />
            )}
          </TableCell>
        )
      },
    },
    {
      align: 'right',
      field: 'collectionEvents',
      headerName: 'Collection Events',
      flex: 1,
      minWidth: 70,
      headerAlign: 'right',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          variant="smallLabel"
          sx={{ textWrap: 'wrap' }}
          color={theme.palette.Text.Secondary}
          textAlign="right"
        >
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        return (
          <TableCell>
            <Typography variant="body">{row.count}</Typography>
          </TableCell>
        )
      },
    },
    {
      align: 'right',
      field: 'systemsCollection',
      headerName: 'Systems Collecting',
      flex: 1,
      minWidth: 70,
      headerAlign: 'right',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams) => (
        <Typography
          variant="smallLabel"
          sx={{ textWrap: 'wrap' }}
          color={theme.palette.Text.Secondary}
          textAlign="right"
        >
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<EventsByClassificationCount>) => {
        return (
          <TableCell>
            {row.dataSystemCount ? (
              <Tooltip
                placement="bottom"
                title={
                  <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                    {row?.dataSystems?.map(dataSystem => (
                      <Typography key={dataSystem?.code}>{dataSystem?.name || dataSystem?.code}</Typography>
                    ))}
                  </Box>
                }
              >
                <Box>
                  <Chip clickable label={row.dataSystemCount} size="small" />
                </Box>
              </Tooltip>
            ) : (
              <Typography>Unknown</Typography>
            )}
          </TableCell>
        )
      },
    },
  ]

  return columns
}
